import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Home, Briefcase, Map } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import LocationPanel from './LocationPanel';
import FullscreenView from './FullscreenView';
import ChatPanel from './ChatPanel';

const ImmersiveLayout = ({
  viewMode,
  facilities,
  facilitiesTotal,
  loadingMore,
  onLoadMoreFacilities,
  products,
  productFilters,
  selectedProduct,
  messages,
  chatExpanded,
  userLocations,
  isLoading,
  onChatToggle,
  onSendMessage,
  onViewChange,
  onLocationUpdate,
  onCheckout,
  checkoutProduct
}) => {
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Handle location updates
  const handleLocationUpdate = (locationType, location) => {
    onLocationUpdate(locationType, location);
    setShowLocationModal(false);
  };

  return (
    <div className="immersive-layout">
      <Toaster position="top-right" />

      {/* Top Navigation Bar */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="top-nav"
      >
        <div className="logo-section">
          <span className="logo">🏋️ FitPass Store</span>
          <span className="tagline">AI-Powered Multi-Sport Card Assistant</span>
        </div>

        <div className="quick-actions">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="action-btn map-btn"
            onClick={() => onViewChange('map')}
          >
            <Map size={20} />
            <span>Mapa obiektów</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="action-btn location-btn"
            onClick={() => setShowLocationModal(true)}
          >
            <MapPin size={20} />
            <span>Moja lokalizacja</span>
          </motion.button>

          {userLocations.home && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="location-badge"
            >
              <Home size={16} />
              <span>{userLocations.home.city}</span>
            </motion.div>
          )}

          {userLocations.work && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="location-badge"
            >
              <Briefcase size={16} />
              <span>{userLocations.work.city}</span>
            </motion.div>
          )}
        </div>
      </motion.header>

      {/* Main Content Area - Chat on left, Products on right */}
      <div className="content-area">
        {/* Left Side - Chat Panel */}
        <aside className="chat-sidebar">
          <ChatPanel
            expanded={true}
            onToggle={onChatToggle}
            messages={messages}
            onSendMessage={onSendMessage}
            onViewChange={onViewChange}
            userLocations={userLocations}
            isLoading={isLoading}
            onCheckout={onCheckout}
            checkoutProduct={checkoutProduct}
          />
        </aside>

        {/* Right Side - Main View */}
        <main className="main-view-container">
          <FullscreenView
            viewMode={viewMode}
            facilities={facilities}
            facilitiesTotal={facilitiesTotal}
            loadingMore={loadingMore}
            onLoadMoreFacilities={onLoadMoreFacilities}
            products={products}
            productFilters={productFilters}
            selectedProduct={selectedProduct}
            onViewChange={onViewChange}
            userLocations={userLocations}
          />
        </main>
      </div>

      {/* Location Modal */}
      <AnimatePresence>
        {showLocationModal && (
          <LocationPanel
            onClose={() => setShowLocationModal(false)}
            onLocationUpdate={handleLocationUpdate}
            currentLocations={userLocations}
          />
        )}
      </AnimatePresence>

      <style jsx>{`
        .immersive-layout {
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .top-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 2rem;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          z-index: 100;
          flex-shrink: 0;
        }

        .logo-section {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
        }

        .logo {
          font-size: 1.25rem;
          font-weight: 700;
          color: #667eea;
        }

        .tagline {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .quick-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .map-btn {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .location-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.375rem 0.75rem;
          background: #f3f4f6;
          border-radius: 20px;
          font-size: 0.75rem;
          color: #374151;
        }

        .content-area {
          flex: 1;
          display: flex;
          overflow: hidden;
        }

        .chat-sidebar {
          width: 380px;
          min-width: 380px;
          background: rgba(255, 255, 255, 0.98);
          display: flex;
          flex-direction: column;
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
          z-index: 50;
        }

        .main-view-container {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
        }

        @media (max-width: 1024px) {
          .chat-sidebar {
            width: 320px;
            min-width: 320px;
          }
        }

        @media (max-width: 768px) {
          .content-area {
            flex-direction: column;
          }

          .chat-sidebar {
            width: 100%;
            min-width: 100%;
            height: 50%;
            order: 2;
          }

          .main-view-container {
            height: 50%;
            order: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ImmersiveLayout;
